// About.jsx - Who we are, in the fewest words that are actually true.
//
// Nothing on this page claims an award, a rating, or a number we cannot evidence.
// The whole argument of the business is that we say checkable things, and an
// about page full of invented statistics would undo it.

import { Link } from "react-router";

import { Seo, JsonLd, organisationSchema, breadcrumbSchema } from "../lib/seo.jsx";
import { useAsync } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import { PageHead, CtaBand, StatRow, MobileCta, Figure } from "./parts.jsx";
import { KIT } from "../lib/media.js";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { UsMap } from "../ui/illustrations.jsx";
import { Phone, Envelope, ArrowRight } from "../ui/icons.jsx";

const PRINCIPLES = [
  ["Show the arithmetic", "Every weight, every deduction, every test. If somebody cannot check our number, our number is worth nothing."],
  ["Say no clearly", "If something is plated, or worth more as an object than as metal, we say so and send it back. A small sale we should not have made costs us more than it makes."],
  ["Never make declining expensive", "Free insured return on anything that is genuinely precious metal, packed as it arrived, no fee and no phone call. The moment declining has a cost, an offer stops being an offer."],
  ["Answer the phone", "A real person, in Michigan, during the hours on this page. Most of what we do is people worried about something in the post, and that is not a chat widget problem."],
];

export default function About() {
  const { data: settings } = useAsync(() => publicApi.settings(), []);
  const company = settings?.company;

  return (
    <>
      <Seo
        title="About us"
        path="/about"
        description="International Gold and Silver buys gold, silver and platinum by mail from people across the United States. Who we are, how we work, and how to reach a person."
      />
      <JsonLd data={organisationSchema()} />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "About", to: "/about" }])} />

      <PageHead
        eyebrow="About us"
        title="We buy metal, and we write everything down."
        lede="International Gold and Silver buys gold, silver, platinum and palladium by mail from people all over the country, and in person at shows. Most of what arrives here is one drawer of jewellery belonging to somebody who died or somebody who is moving on."
        art={() => (
          <Figure
            src={KIT.mailerRight}
            alt="The free appraisal mailers we send out"
            sizes="(min-width: 1024px) 26vw, 60vw"
            className="max-w-[21rem] ml-auto"
            priority
          />
        )}
      />

      <div className="shell pb-14 md:pb-20">
        <div className="grid gap-9 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16">
          <Reveal className="prose-igs">
            <p>
              We started buying at coin and antique shows, across a table, where a person could watch the
              scale and ask what a number meant. That works because everything is visible.
            </p>
            <p>
              Mail-in buying broke that, and most of the industry was happy to let it stay broken. A package
              disappears into a building, a number comes back, and the seller has no way of knowing what
              happened in between. So we rebuilt the visible part: every package is opened on camera, every
              piece is weighed and tested on its own, and every one of those numbers goes on a page the
              customer can open at eleven at night.
            </p>
            <p>
              We are a small business. You will speak to the same handful of people every time you call, and
              one of them will have been the one holding your ring.
            </p>

            <h2>What we hold ourselves to</h2>
          </Reveal>

          <Reveal as="aside" from="right" delay={110} className="lg:pt-2">
            <div className="panel p-5 sm:p-6">
              <p className="eyebrow mb-4">Reach a person</p>
              <a href="tel:+1-401-315-4653" className="flex items-center gap-2.5 text-[15px] font-semibold text-ink link">
                <Phone size={16} className="text-bronze" /> {company?.phone || "(401) 315-4653"}
              </a>
              <a href={`mailto:${company?.email || "orders@internationalgoldandsilver.com"}`} className="flex items-center gap-2.5 mt-3 text-[15px] text-ink link break-all">
                <Envelope size={16} className="text-bronze shrink-0" /> {company?.email || "orders@internationalgoldandsilver.com"}
              </a>
              <p className="hint mt-4 pt-4 border-t border-rule leading-relaxed">
                {company?.supportHours || "Monday to Friday, 9am to 6pm Eastern"}
              </p>

              {company?.address && (
                <address className="hint mt-4 pt-4 border-t border-rule not-italic leading-relaxed">
                  {company.address.line1}<br />
                  {company.address.city}, {company.address.state} {company.address.postal}
                </address>
              )}

              <Link to="/contact" className="btn btn-quiet w-full mt-5">Send a message</Link>
            </div>
            <UsMap className="mt-6 hidden lg:block" />
          </Reveal>
        </div>

        <StaggerBoxed
          as="ol"
          step={100}
          className="grid gap-px bg-rule border border-rule sm:grid-cols-2 mt-8"
          itemClassName="bg-paper h-full"
        >
          {PRINCIPLES.map(([title, body], index) => (
            <li key={title} className="bg-paper p-6 sm:p-7 h-full">
              <span className="numeral text-[2.4rem] block mb-4">{String(index + 1).padStart(2, "0")}</span>
              <h3 className="display display-sm text-ink">{title}</h3>
              <p className="hint mt-2.5 leading-relaxed">{body}</p>
            </li>
          ))}
        </StaggerBoxed>
      </div>

      <StatRow
        items={[
          { label: "Where we are", value: "Michigan", hint: "Packages are opened and evaluated here" },
          { label: "Where we buy from", count: 50, suffix: " states", hint: "Plus shows across the Midwest and East" },
          { label: "Packages opened on camera", value: "Every one", hint: "Without exception, since day one" },
        ]}
      />

      <div className="shell band">
        <div className="grid gap-7 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <h2 className="display display-lg text-ink">Meet us in person instead.</h2>
            <p className="lede mt-4 measure text-muted">
              We buy at shows across the Midwest and the East Coast. If one is near you, bring your items
              and watch the scale yourself. Call and we will tell you where we will be.
            </p>
          </Reveal>
          <Reveal delay={110} className="flex flex-col xs:flex-row flex-wrap gap-3 lg:justify-end">
            <a href="tel:+1-401-315-4653" className="btn btn-outline btn-lg"><Phone size={17} /> Call about shows</a>
            <Link to="/get-a-kit" className="btn btn-primary btn-lg">Get a free kit <ArrowRight size={17} /></Link>
          </Reveal>
        </div>
      </div>

      <CtaBand />
      <MobileCta />
    </>
  );
}
